import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Check, Plus, MapPin, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { PlacesAutocomplete } from "@/components/PlacesAutocomplete";
import LocationPinMap from "@/components/LocationPinMap";

import { logger } from "@/lib/logger";
/**
 * AddressPicker — reusable across Profile / Product page / Cart.
 * Presents a dropdown of the customer's saved addresses + an inline "Add new address" flow.
 * Notifies parent via onChange(address_id, address_object).
 */
export function AddressPicker({ value, onChange, testidPrefix = "addr-picker", compact = false }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(null);   // saved address whose pin must be confirmed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/addresses");
      setAddresses(data);
      return data;
    } catch (e) {
      logger.warn("Failed to load addresses in picker:", e);
      return [];
    } finally { setLoading(false); }
  }, []);

  // On first mount, load addresses and auto-select the default one if none picked yet.
  useEffect(() => {
    let cancelled = false;
    load().then((data) => {
      if (cancelled) return;
      if (!value && data.length > 0) {
        const def = data.find(a => a.is_default) || data[0];
        onChange(def.id, def);
      }
    });
    return () => { cancelled = true; };
    // Intentionally run only on mount — `value`/`onChange` come from the parent
    // and would otherwise re-trigger the address fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = addresses.find(a => a.id === value);

  if (loading && addresses.length === 0) {
    return <div className="text-sm text-stone-500 py-3"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading addresses…</div>;
  }

  if (addresses.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-marigold/40 bg-marigold/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-4 w-4 text-marigold" />
          <div className="text-sm font-semibold text-stoneWarm">No saved delivery addresses yet</div>
        </div>
        <p className="text-xs text-stone-600 mb-3">Add one right here — it's saved to your profile too, no need to leave the page.</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid={`${testidPrefix}-inline-add-btn`} className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
              <Plus className="h-3 w-3 mr-1" /> Add new address
            </Button>
          </DialogTrigger>
          {dialogOpen && (
            <AddressForm
              onDone={async (newAddr) => {
                setDialogOpen(false);
                await load();
                if (newAddr) onChange(newAddr.id, newAddr);
              }}
              testidPrefix={testidPrefix}
            />
          )}
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-wrap`}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select value={value || ""} onValueChange={(v) => { const a = addresses.find(x => x.id === v); onChange(v, a); if (a?.needs_location) setConfirming(a); }}>
            <SelectTrigger data-testid={`${testidPrefix}-select`} className={compact ? "h-11" : "h-12"}>
              <SelectValue placeholder="Select delivery address" />
            </SelectTrigger>
            <SelectContent>
              {addresses.map(a => (
                <SelectItem key={a.id} value={a.id} data-testid={`${testidPrefix}-option-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{a.label}</span>
                    {a.is_default && <Star className="h-3 w-3 text-teal fill-teal" />}
                    <span className="text-xs text-stone-500 truncate max-w-xs">— {[a.flat_no, a.address].filter(Boolean).join(", ")}, {a.pincode}</span>
                    {a.needs_location && <span className="text-[10px] text-marigold font-semibold">Confirm pin</span>}
                    {!a.needs_location && a.serviceability_status === "not_covered" && <span className="text-[10px] text-terracotta font-semibold">Not delivered</span>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`${testidPrefix}-add-btn`} className="rounded-full h-11">
              <Plus className="h-3 w-3 mr-1" /> New
            </Button>
          </DialogTrigger>
          {dialogOpen && (
            <AddressForm
              onDone={async (newAddr) => {
                setDialogOpen(false);
                await load();
                if (newAddr) onChange(newAddr.id, newAddr);
              }}
              testidPrefix={testidPrefix}
            />
          )}
        </Dialog>
      </div>
      {selected && !compact && (
        <div className="text-xs text-stone-500 pl-1">
          Delivering to: <span className="text-stone-700">{[selected.flat_no, selected.address, selected.city, selected.pincode].filter(Boolean).join(", ")}</span>
        </div>
      )}
      {selected?.needs_location && (
        <button type="button" onClick={() => setConfirming(selected)} data-testid={`${testidPrefix}-confirm-pin`}
          className="w-full text-left rounded-xl border border-marigold/40 bg-marigold/10 p-2.5 text-xs text-stoneWarm">
          <b>Confirm this address on the map</b> so the rider can find you — tap to set the pin.
        </button>
      )}
      {selected && !selected.needs_location && selected.serviceability_status === "not_covered" && (
        <div className="rounded-xl border border-terracotta/40 bg-terracotta/5 p-2.5 text-xs text-terracotta" data-testid={`${testidPrefix}-not-covered`}>
          No shop delivers to this address yet. Pick another address.
        </div>
      )}
      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        {confirming && (
          <AddressForm
            editing={confirming}
            testidPrefix={`${testidPrefix}-confirm`}
            onDone={async (updated) => {
              setConfirming(null);
              await load();
              if (updated) onChange(updated.id, updated);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

const LABELS = ["Home", "Work", "Other"];

/** AddressForm — search the address (Google Places), fine-tune the pin on the
 *  map, then add the details a rider needs (flat no., landmark). Delivery
 *  notes are given per order at checkout (E-C2).
 *  Every saved address has an exact location; we tell the customer right away
 *  whether any shop delivers there. */
/** Street / locality part of a Google address — without the trailing
 *  "City, State PIN, India" that we already store separately. */
// Google "plus codes" (5747+M57) name places without a street address: never show them.
const PLUS_CODE = /(^|,\s*)[23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{2,3}\b,?\s*/gi;
export function streetLine(p) {
  let a = (p.formatted_address || "").replace(PLUS_CODE, (m, lead, off) => (off ? lead : "")).replace(/,\s*India$/i, "");
  if (p.pincode) a = a.replace(new RegExp(`\\s*${p.pincode}$`), "");
  if (p.state) a = a.replace(new RegExp(`,\\s*${p.state.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i"), "");
  if (p.city) a = a.replace(new RegExp(`,\\s*${p.city.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i"), "");
  a = a.trim().replace(/^,|,$/g, "").trim();
  // Nothing but the city left: use the area name (e.g. "Badlapur Gaon"); the customer can edit it.
  return a && a.toLowerCase() !== (p.city || "").toLowerCase() ? a : (p.area || a);
}

export function AddressForm({ onDone, testidPrefix = "addr-form", editing = null }) {
  const [f, setF] = useState({
    label: editing?.label || "Home",
    flat_no: editing?.flat_no || "",
    address: editing?.address || "",
    landmark: editing?.landmark || "",
    instructions: editing?.instructions || "",
    city: editing?.city || "",
    state: editing?.state || "",
    pincode: editing?.pincode || "",
    area: editing?.area || "",
    is_default: editing?.is_default || false,
  });
  const hadExactPin = editing && !editing.needs_location && editing.lat != null;
  const [pin, setPin] = useState(editing?.lat != null ? { lat: editing.lat, lng: editing.lng } : null);
  const [pinConfirmed, setPinConfirmed] = useState(!!hadExactPin);
  const [source, setSource] = useState(editing?.location_source && editing.location_source !== "backfill" ? editing.location_source : "pin");
  const [placeId, setPlaceId] = useState(editing?.place_id || null);
  const [search, setSearch] = useState("");
  const [pinText, setPinText] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pinFromMap, setPinFromMap] = useState(!!(editing?.pincode && editing?.lat != null));
  const addressTouched = useRef(!!editing?.address);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const customLabel = !LABELS.includes(f.label);

  // Fill address fields from Google without overwriting what the customer typed.
  const applyPlace = useCallback((p) => {
    setF(prev => ({
      ...prev,
      address: addressTouched.current && prev.address ? prev.address : (streetLine(p) || prev.address),
      city: p.city || prev.city,
      state: p.state || prev.state,
      // The pincode always follows the map pin; it can only be typed when
      // Google has no pincode for this spot.
      pincode: p.pincode || "",
      area: p.area || prev.area,
    }));
    setPinFromMap(!!p.pincode);
    if (p.formatted_address) setPinText(p.formatted_address);
  }, []);

  // Pin moved on the map (or current location): look up the address for it.
  const reverseTimer = useRef(null);
  const onPinMove = useCallback((p, how = "pin") => {
    setPin(p);
    setPinConfirmed(true);
    setSource(how);
    setPlaceId(null);
    clearTimeout(reverseTimer.current);
    reverseTimer.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const { data } = await api.get("/geocode/reverse", { params: { lat: p.lat, lng: p.lng } });
        if (data.ok) applyPlace(data);
      } catch (e) { logger.warn("Reverse geocode failed:", e); }
      finally { setLookingUp(false); }
    }, 500);
  }, [applyPlace]);
  useEffect(() => () => clearTimeout(reverseTimer.current), []);

  // Live coverage for the pin.
  useEffect(() => {
    if (!pin) { setCoverage(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.get("/serviceability/check", { params: { lat: pin.lat, lng: pin.lng } })
        .then(({ data }) => { if (alive) setCoverage(data); })
        .catch(() => {});
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [pin]);

  const submit = async () => {
    if (!pin || !pinConfirmed) return toast.error("Set your delivery location on the map first");
    if (!f.flat_no.trim()) return toast.error("Add your flat / house number so the rider can find you");
    if (f.address.trim().length < 3) return toast.error("Add the street / area");
    if (!/^\d{6}$/.test(f.pincode)) return toast.error("Enter a valid 6-digit pincode");
    if (!f.label.trim()) return toast.error("Give this address a name");
    setBusy(true);
    try {
      const payload = {
        ...f,
        area: f.area.trim() || null, landmark: f.landmark.trim() || null, instructions: f.instructions.trim() || null,
        city: f.city.trim() || null, state: f.state.trim() || null,
        lat: pin.lat, lng: pin.lng, place_id: placeId, location_source: source,
      };
      const { data } = editing
        ? await api.patch(`/customer/addresses/${editing.id}`, payload)
        : await api.post("/customer/addresses", payload);
      toast.success(editing ? "Address updated" : "Address saved to your profile");
      onDone(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">{editing ? (editing.needs_location ? "Confirm your location" : "Edit address") : "Add address"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        {editing?.needs_location && (
          <div className="rounded-xl border border-marigold/40 bg-marigold/10 p-3 text-xs text-stoneWarm" data-testid={`${testidPrefix}-confirm-note`}>
            We've placed the pin roughly from your address. Move the map so the pin sits exactly on your building, then save.
          </div>
        )}
        <div>
          <Label>Search your address</Label>
          <div className="mt-1">
            <PlacesAutocomplete
              value={search}
              onChange={setSearch}
              near={pin}
              onPlaceSelected={(p) => {
                setPin({ lat: p.lat, lng: p.lng });
                setPinConfirmed(true);
                setSource("search");
                setPlaceId(p.place_id || null);
                addressTouched.current = false;
                applyPlace(p);
              }}
              dataTestid={`${testidPrefix}-search`}
              placeholder="Building, street, society or landmark…"
            />
          </div>
        </div>

        <LocationPinMap value={pin} onMove={onPinMove} testid={`${testidPrefix}-map`} />
        {pin && (
          <div className="text-xs text-stone-600 flex items-start gap-1.5" data-testid={`${testidPrefix}-pin-text`}>
            <MapPin className="h-3.5 w-3.5 mt-0.5 text-terracotta shrink-0" />
            <span>{lookingUp ? "Finding the address for this pin…" : (pinText || `Pinned at ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`)}</span>
          </div>
        )}
        {coverage && (
          <div
            data-testid={`${testidPrefix}-coverage`}
            className={`text-xs rounded-lg px-3 py-2 ${coverage.serviceable ? "bg-teal/10 text-teal" : "bg-terracotta/10 text-terracotta"}`}
          >
            {coverage.serviceable
              ? <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />{coverage.nearby_shops} shop{coverage.nearby_shops === 1 ? "" : "s"} deliver here</span>
              : "No shop delivers to this spot yet. You can still save it — we'll let you know when one does."}
          </div>
        )}

        <div>
          <Label>Flat / House no., floor, building *</Label>
          <Input data-testid={`${testidPrefix}-flat`} value={f.flat_no} maxLength={80} onChange={(e) => set("flat_no", e.target.value)} placeholder="e.g. B-402, 4th floor, Sea Breeze Apts" className="h-11 mt-1" />
        </div>
        <div>
          <Label>Street / area *</Label>
          <Input data-testid={`${testidPrefix}-address`} value={f.address} maxLength={300} onChange={(e) => { addressTouched.current = true; set("address", e.target.value); }} placeholder="Filled from the map — edit if needed" className="h-11 mt-1" />
        </div>
        <div>
          <Label>Nearby landmark <span className="text-stone-400 font-normal">(optional)</span></Label>
          <Input data-testid={`${testidPrefix}-landmark`} value={f.landmark} maxLength={120} onChange={(e) => set("landmark", e.target.value)} placeholder="e.g. Opposite Infiniti Mall" className="h-11 mt-1" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Pincode *</Label>
            <Input data-testid={`${testidPrefix}-pincode`} value={f.pincode} inputMode="numeric" readOnly={pinFromMap}
              title={pinFromMap ? "Set from the map pin — move the pin to change it" : undefined}
              placeholder={pin ? "" : "From map"}
              onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={`h-10 mt-1 font-mono ${pinFromMap ? "bg-stone-100 text-stone-600" : ""}`} />
          </div>
          <div><Label className="text-xs">City</Label><Input data-testid={`${testidPrefix}-city`} value={f.city} onChange={(e) => set("city", e.target.value)} className="h-10 mt-1" /></div>
          <div><Label className="text-xs">State</Label><Input data-testid={`${testidPrefix}-state`} value={f.state} onChange={(e) => set("state", e.target.value)} className="h-10 mt-1" /></div>
        </div>
        <div>
          <Label>Save as</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {LABELS.map(l => (
              <button type="button" key={l} data-testid={`${testidPrefix}-label-${l.toLowerCase()}`}
                onClick={() => set("label", l === "Other" ? "" : l)}
                className={`px-3 py-1.5 rounded-full text-sm border ${(l === "Other" ? customLabel : f.label === l) ? "border-teal bg-teal/10 text-teal" : "border-stone-200 text-stone-600"}`}>
                {l}
              </button>
            ))}
          </div>
          {customLabel && (
            <Input data-testid={`${testidPrefix}-label`} value={f.label} maxLength={40} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Mom's place" className="h-10 mt-2" />
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.is_default} onChange={(e) => set("is_default", e.target.checked)} className="h-4 w-4" data-testid={`${testidPrefix}-default`} />
          Set as default delivery address
        </label>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid={`${testidPrefix}-save`} className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Save address"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
